import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, PenLine, Search } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TAB_ROUTES } from "@/context/TabsContext";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/api";

export function MobileNavDrawer({ open, onOpenChange, onSearch, onSignature }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const go = (path) => { onOpenChange(false); navigate(path); };
  const isActive = (to) => location.pathname === to || (to !== "/" && location.pathname.startsWith(to));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[85vw] max-w-xs p-0 flex flex-col" data-testid="mobile-nav-drawer">
        <SheetHeader className="p-4 border-b border-border text-left">
          <SheetTitle className="font-display text-xl">Menú</SheetTitle>
          <div className="flex items-center gap-3 pt-2">
            <div className="w-9 h-9 rounded-sm bg-secondary flex items-center justify-center font-display font-bold text-sm">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{ROLE_LABEL[user?.role] || user?.role}</div>
            </div>
          </div>
        </SheetHeader>
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {TAB_ROUTES.map((it) => {
            const Icon = it.icon; const active = isActive(it.path);
            return (
              <button key={it.path} type="button" data-testid={`mobile-nav-${it.key}`} onClick={() => go(it.path)}
                className={`w-full flex items-center gap-3 h-12 px-3 rounded-sm text-sm transition-colors ${active ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                <Icon className="w-5 h-5 shrink-0" /> {it.label}
              </button>
            );
          })}
          <div className="border-t border-border my-2" />
          <button type="button" data-testid="mobile-nav-search" onClick={() => { onOpenChange(false); onSearch(); }} className="w-full flex items-center gap-3 h-12 px-3 rounded-sm text-sm hover:bg-muted">
            <Search className="w-5 h-5" /> Buscar pacientes o citas
          </button>
          <button type="button" data-testid="mobile-nav-signature" onClick={() => { onOpenChange(false); onSignature(); }} className="w-full flex items-center gap-3 h-12 px-3 rounded-sm text-sm hover:bg-muted">
            <PenLine className="w-5 h-5" /> Mi firma
          </button>
        </nav>
        <div className="p-2 border-t border-border">
          <button data-testid="mobile-logout" onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center gap-3 h-12 px-3 rounded-sm text-sm text-destructive hover:bg-destructive/10">
            <LogOut className="w-5 h-5" /> Cerrar sesión
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (to) => location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
  const items = TAB_ROUTES.filter((r) => r.key !== "contabilidad");
  return (
    <nav data-testid="mobile-bottom-nav" className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-card/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]">
      <div className="grid grid-cols-5">
        {items.map((it) => {
          const Icon = it.icon; const active = isActive(it.path);
          return (
            <button key={it.path} type="button" data-testid={`bottom-nav-${it.key}`} onClick={() => navigate(it.path)} aria-current={active ? "page" : undefined}
              className={`h-14 flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider transition-colors ${active ? "text-primary font-bold" : "text-muted-foreground"}`}>
              <Icon className={`w-5 h-5 ${active ? "scale-110" : ""} transition-transform`} />
              {it.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
